// The mask formats of ADR-066 §5, implemented in the browser for privacy mode (task 402.10). They mirror the
// api's ValueMasker exactly, because an operator who turns privacy mode on and off must not see two different
// shortenings of the same value — and because a masked value that arrives from the api already carries the
// sentinel and must pass through untouched.
//
// The formats themselves are owner decisions (Q1, 2026-09-21): IBAN first 4 and last 4, a person's name as first
// name plus an initial, a date of birth as its year, a tax identifier as its last 4, and country fields never.

export const BULLET = '•';
export const HIDDEN = `${BULLET}${BULLET}${BULLET}${BULLET}`;

export const MASK_FORMATS = {
  first4last4: 'first4last4',
  firstNameLastInitial: 'firstNameLastInitial',
  initial: 'initial',
  yearOnly: 'yearOnly',
  last4: 'last4',
  email: 'email',
  hidden: 'hidden',
} as const;

export type MaskFormat = (typeof MASK_FORMATS)[keyof typeof MASK_FORMATS];

/** A value the api already masked carries the sentinel; masking it again would only damage it. */
export function isMasked(value: string): boolean {
  return value.includes(BULLET);
}

function alphanumeric(value: string): string {
  return value.replace(/[^a-zA-Z0-9]/g, '');
}

function initial(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length === 0) return trimmed;
  if (trimmed.length === 2 && trimmed[1] === '.') return trimmed;
  return `${trimmed[0]!.toUpperCase()}.`;
}

/**
 * Shortens one value by one format. Null, empty and already-masked values pass through, so the function is safe
 * to apply to anything on a page and idempotent when a value crosses two layers.
 */
export function maskValue(value: unknown, format: string | undefined | null): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  if (text.length === 0 || isMasked(text)) return text;

  switch (format) {
    case MASK_FORMATS.first4last4: {
      const compact = alphanumeric(text);
      return compact.length <= 8 ? HIDDEN : `${compact.slice(0, 4)}${HIDDEN}${compact.slice(-4)}`;
    }
    case MASK_FORMATS.last4: {
      const compact = alphanumeric(text);
      return compact.length <= 4 ? HIDDEN : `${HIDDEN}${compact.slice(-4)}`;
    }
    case MASK_FORMATS.firstNameLastInitial: {
      const parts = text.split(' ').filter((p) => p.length > 0);
      if (parts.length <= 1) return text.trim();
      return `${parts[0]} ${initial(parts[parts.length - 1]!)}`;
    }
    case MASK_FORMATS.initial:
      return initial(text);
    case MASK_FORMATS.yearOnly: {
      const trimmed = text.trim();
      if (/^\d{4}$/.test(trimmed)) return trimmed;
      const parsed = new Date(trimmed);
      return Number.isNaN(parsed.getTime()) ? HIDDEN : String(parsed.getUTCFullYear());
    }
    case MASK_FORMATS.email: {
      const at = text.indexOf('@');
      if (at <= 0 || at === text.length - 1) return HIDDEN;
      return `${text[0]}${BULLET}${BULLET}${BULLET}@${text.slice(at + 1)}`;
    }
    case MASK_FORMATS.hidden:
    default:
      // An unknown format hides the value rather than showing it: privacy mode errs towards covering more.
      return HIDDEN;
  }
}
